import { useMutation, useQuery, useQueryClient as useQueryClientV3 } from 'react-query';
import { useQueryClient as useQueryClientV4 } from '@tanstack/react-query-v4';
import apiFetch, { ApiFetchOptions } from '@scale/frontend-lib/api-fetch';
import { PauseCapabilityApiResponse } from '@scale/shared/types/timer';

export function usePauseCapability(taskId: string) {
  const queryClientV3 = useQueryClientV3();
  const queryClientV4 = useQueryClientV4();

  const { data, isSuccess, refetch, isLoading } = useQuery(
    ['pauseCapability', taskId],
    ({ signal }) => fetchPauseCapabilityAndState(taskId, { signal }),
    {
      enabled: !!taskId,
      initialData: {
        Success: false,
        Paused: false,
        pausedDuration: 0,
        pausedAt: 0,
        disabledAutoPause: true,
        totalPausedTimeSeconds: 0,
      },
    },
  );

  const mutation = useMutation(
    async (pause: boolean) => {
      await apiFetch(null, `internal/timer/${pause ? 'pause' : 'resume'}/${taskId}`, {
        method: 'POST',
      });
    },
    {
      onSuccess: () => {
        // trigger a fresh refetch of pauseCapability
        // cancel any previously running query to prevent race conditions
        // note: 'pauseCapability' query is currently on react-query V3
        queryClientV3.cancelQueries(['pauseCapability', taskId]);
        queryClientV3.invalidateQueries(['pauseCapability', taskId]);

        // Also invalidate checkClaim query so maxClaim updates in InfoDeadline and other components
        // note: 'checkClaim' query is currently on react-query V4
        queryClientV4.invalidateQueries(['checkClaim']);
      },
    },
  );

  // TODO: Implement reason / initiator for pausing. A task can be auto-pause and paused by the user as well.
  const handlePauseClick = () => {
    if (!data) {
      return;
    }
    mutation.mutate(!data.Paused);
  };

  return {
    isPaused: isSuccess && data?.Paused,
    showPauseButton: isSuccess && data?.Success,
    pausedDuration: data?.pausedDuration ?? 0,
    pausedAt: data?.pausedAt ?? 0,
    disabledAutoPause: data?.disabledAutoPause ?? true,
    handlePauseClick,
    isLoading: mutation.isLoading || isLoading,
    refetch,
    totalPausedTimeSeconds: data?.totalPausedTimeSeconds ?? 0,
    pauseTimeClaimTimeExtensionInfo: data?.pauseTimeClaimTimeExtensionInfo,
  };
}

async function fetchPauseCapabilityAndState(
  taskId: string,
  options?: ApiFetchOptions,
): Promise<PauseCapabilityApiResponse> {
  if (!taskId) {
    return { Success: false, Paused: false } as unknown as PauseCapabilityApiResponse;
  }

  const response = await apiFetch(null, `internal/timer/pause/capability/${taskId}`, {
    ...options,
    method: 'GET',
  });
  return response;
}
